import React, { useContext, useEffect, useState } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { AuthContext } from '../../commons/auth/AuthContext';
import { getUrlForRetentionForm } from '../../commons/UrlUtils';
import {
    submitConsentResponse,
    logRetentionUserAgentInfo,
    sessionExpiryCheck,
} from '../retentionRoutes';
import { getPlatformInfo } from '../../commons/UserAgentInfo';
import ConfirmFormSubmissionPopup from '../../commons/ConfirmFormSubmissionPopup';
import { RetentionForm } from './RetentionForm';
import { useSessionExpiry } from '../../commons/auth/sessionExpiryHook';

const RetentionFormContainer = (props) => {
    const [userEnteredData, setUserEnteredData] = useState();
    const [formSubmissionError, setFormSubmissionError] = useState();
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [userConfirmationPopupVisible, setUserConfirmationPopupVisibility] =
        useState(false);

    const authContext = useContext(AuthContext);
    const routeMatch = useRouteMatch();
    const { url } = routeMatch.params;
    const history = useHistory();

    useSessionExpiry(url, authContext, sessionExpiryCheck);

    // execute effect on first render.
    useEffect(() => {
        logRetentionUserAgentInfo(
            url,
            getPlatformInfo('app.retentionForm'),
            authContext
        ).catch(console.error);
    }, []);

    // when the form is submitted, a confirmation pop-up is shown
    const _requestUserConfirmation = (formValues) => {
        setUserConfirmationPopupVisibility(true);
        setUserEnteredData(formValues);
    };

    // finally, submitting the details to backend
    const _submitResponse = () => {
        setFormSubmitting(true);
        submitConsentResponse(url, userEnteredData, authContext)
            .then(() => {
                setFormSubmitting(false);
                history.push(getUrlForRetentionForm(url), {
                    redirectAfterResponseReceived: true,
                });
            })
            .catch((error) => {
                setFormSubmitting(false);
                if (error?.response?.status !== 401) {
                    // set submission error only if it is not a result of 401 expired token call.
                    // as user would be displayed SessionExpired popup to re-login and try again.
                    setFormSubmissionError(
                        'Error submitting response. Please try again later.'
                    );
                }
            });
    };

    const _closePopup = () => {
        setUserConfirmationPopupVisibility(false);
        setFormSubmissionError(null);
    };

    const _renderConfirmationMessage = () => (
        <span>Do you want to proceed with submitting the response?</span>
    );

    return (
        <div className="max-w-4xl mx-auto bg-white px-6 py-4 md:mt-6 md:mb-6 rounded shadow-md text-sm">
            <div className="w-full bg-white text-lg text-center py-2 font-semibold text-customGreen leading-relaxed">
                Thank you for your prior participation in the D. E. Shaw
                group&apos;s Industry Consulting Program (ICP)
                <br />
                <br />
            </div>
            <div>
                Please complete this form to let us know if you are interested
                in potential future participation in the ICP:
            </div>
            <br />
            <RetentionForm
                onSubmit={_requestUserConfirmation}
                consultantCountry={props.consultantCountry}
            />
            <ConfirmFormSubmissionPopup
                popUpShown={userConfirmationPopupVisible}
                onCancel={_closePopup}
                onSubmit={_submitResponse}
                errorMessage={formSubmissionError}
                submitting={formSubmitting}
                header="Confirm Response Submission"
                submitButtonText="Submit Response"
                renderConfirmationMessage={_renderConfirmationMessage()}
            />
        </div>
    );
};

export default RetentionFormContainer;

RetentionFormContainer.propTypes = {
    consultantCountry: PropTypes.string,
};
